import { useState, useCallback, useEffect } from "react";
import { api } from "@/lib/api";

/**
 * useMode — fetches and persists the current user's mode (group | private).
 *
 * Returns:
 *   mode        — current mode string, null while loading
 *   setMode     — async function to switch mode; updates state immediately
 *   modeLoading — true during the initial fetch
 */
export function useMode() {
  const [mode, setModeState] = useState(null);
  const [modeLoading, setModeLoading] = useState(true);

  // Fetch current mode from the backend on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/mode");
        if (!cancelled) setModeState(data.mode ?? "group");
      } catch {
        if (!cancelled) setModeState("group");
      } finally {
        if (!cancelled) setModeLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const setMode = useCallback(async (newMode) => {
    // Optimistic update so the UI responds instantly
    setModeState(newMode);
    try {
      const { data } = await api.post("/mode", { mode: newMode });
      setModeState(data.mode);
    } catch {
      // If the request fails, revert is not strictly necessary because the
      // backend will return the correct value on the next poll, but we could
      // re-fetch here if desired.
    }
  }, []);

  return { mode, setMode, modeLoading };
}
